/* =========================================================================
   "6 from 120" — where 120 comes from the shop

   Every rail on the home page carries a small count: "6 from 120" above the
   heading, and the same pair again inside the insight pill. The first number
   was roughly right by accident and the second was typed into a section
   setting, so every rail on the page claimed 120 products whether it had
   1,400 behind it or eleven. Nobody would notice it was wrong, which is the
   problem with it — it is presented as a fact.

   So both halves are read from the page and the shop instead:

     shown  ->  the product cards actually in that rail right now, which
                changes when the weather rail narrows itself or a shopper
                answers the row's question
     total  ->  collection.all_products_count, fetched once per collection
                from templates/collection.count.liquid

   The merchant's own wording is left alone. This only rewrites a value that
   is already of the form "<number> from <number>" — anything else is copy
   with an opinion in it and is not ours to touch. Nothing is rewritten
   before the total is in hand, and a request that fails leaves the number
   exactly as the section rendered it, so the worst case is the behaviour we
   already had.

   Deliberately not done in Liquid, though that is where it belongs: the
   section file that renders these pills has stopped syncing to the theme
   (see claude/theme-sync-stuck-file.md). Doing it here also means the count
   can follow the open tab, which a server-rendered number cannot.
   ========================================================================= */
(function () {
  'use strict';

  if (window.VroRailCounts) return;

  /* "6 from 120", and nothing else. A pill reading "5 from 120 picks" or
     "from the archive" is wording, not a count. */
  var PAIR = /^(\d[\d,]*)\s+from\s+(\d[\d,]*)$/;

  var CACHE_PREFIX = 'vro_collection_count:';
  var pending = {};

  function cached(handle) {
    try {
      var raw = sessionStorage.getItem(CACHE_PREFIX + handle);
      if (raw === null) return null;
      var n = parseInt(raw, 10);
      return isFinite(n) ? n : null;
    } catch (e) { return null; }
  }

  function remember(handle, total) {
    try { sessionStorage.setItem(CACHE_PREFIX + handle, String(total)); } catch (e) { /* private mode */ }
  }

  /* One request per collection per session, shared by every pill that wants
     it — a four-tab rail asks for its open tab, and asks again for the next
     one, and the answer to the first is still here. */
  function totalFor(handle) {
    if (!handle) return Promise.resolve(null);
    var hit = cached(handle);
    if (hit !== null) return Promise.resolve(hit);
    if (pending[handle]) return pending[handle];

    pending[handle] = fetch('/collections/' + encodeURIComponent(handle) + '?view=count', {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (res) {
        if (!res.ok) throw new Error('count ' + res.status);
        return res.text();
      })
      .then(function (text) {
        /* The template prints a bare number. Anything else — a redirect to
           a real page, a shop-level error page — is not a count, and a
           number scraped out of the middle of it would be a guess. */
        var trimmed = String(text).trim();
        if (!/^\d+$/.test(trimmed)) throw new Error('count was not a number');
        var n = parseInt(trimmed, 10);
        remember(handle, n);
        return n;
      })
      .catch(function () { return null; })
      .then(function (n) { delete pending[handle]; return n; });

    return pending[handle];
  }

  /* ---------------------------------------------------------------
     Reading and writing the number, without touching anything else
  --------------------------------------------------------------- */

  /* The pill is a diamond, then the words. Only the words are ours, so the
     last text node is what gets rewritten rather than innerHTML — which
     would take the icon with it. */
  function textNodeOf(el) {
    var found = null;
    for (var i = 0; i < el.childNodes.length; i++) {
      var node = el.childNodes[i];
      if (node.nodeType === 3 && node.textContent.trim()) found = node;
    }
    return found;
  }

  function readPair(el) {
    var node = textNodeOf(el);
    if (!node) return null;
    var match = PAIR.exec(node.textContent.trim());
    if (!match) return null;
    return {
      node: node,
      shown: parseInt(match[1].replace(/,/g, ''), 10),
      total: parseInt(match[2].replace(/,/g, ''), 10)
    };
  }

  function writePair(node, shown, total) {
    /* A rail can show more than its collection holds — a hand-picked
       line-up whose View All points at a shelf still being filled — and
       "5 from 3" is not a count anyone can read. The total is never less
       than what is on screen. */
    if (typeof total === 'number' && total < shown) total = shown;
    var next = shown + ' from ' + total;
    if (node.textContent.trim() === next) return;
    /* The surrounding whitespace is the section's, so it is kept: the pill
       is laid out around it. */
    node.textContent = node.textContent.replace(/(\s*)(.*?)(\s*)$/, '$1' + next + '$3');
  }

  /* ---------------------------------------------------------------
     Which rail a number is counting
  --------------------------------------------------------------- */

  function openPane(section) {
    return section.querySelector('.tab-pane.active, .tab-pane.show.active') ||
           section.querySelector('.tab-pane') || section;
  }

  function railIn(root) {
    return root.querySelector('[data-vro-collection]') ||
           root.querySelector('.rail-scroll') || root;
  }

  /* The collection behind a rail, from whatever the section was willing to
     say. data-vro-collection is the rail's own answer; a rail without one
     still closes on a View All tile, and that tile's href names the
     collection it is about to send the shopper to. */
  function collectionIn(href) {
    var parts = String(href || '').split('/collections/');
    return parts.length > 1 ? (parts[1].split('?')[0].split('#')[0].split('/')[0] || null) : null;
  }

  function handleFor(root, section) {
    /* The rail's "Link above the cards" (Hyrox Mode's View All → Health &
       Nutrition) names the shelf the rail stands for — its cards are often
       hand-picked from it — so that collection's size is the total. */
    var top = section && section.querySelector('.feed-rail-top-link');
    var topHandle = top && collectionIn(top.getAttribute('href'));
    if (topHandle) return topHandle;
    var rail = root.querySelector('[data-vro-collection]') ||
               (section && section.querySelector('[data-vro-collection]'));
    if (rail) {
      var named = rail.getAttribute('data-vro-collection');
      if (named) return named;
    }
    var link = (root.querySelector('.feed-view-all') ||
                (section && section.querySelector('.feed-view-all')));
    if (link) return collectionIn(link.getAttribute('href'));
    return null;
  }

  function visibleCards(root) {
    var cards = root.querySelectorAll('.feed-card');
    var n = 0;
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      if (card.hidden) continue;
      if (card.classList.contains('vro-facet-hidden')) continue;
      if (card.classList.contains('vro-weather-hidden')) continue;
      n++;
    }
    return n;
  }

  /* ---------------------------------------------------------------
     One pass over the page
  --------------------------------------------------------------- */

  function paintOne(el, root, section) {
    /* While the row is reporting an answer, the pill belongs to
       assets/vro-subfilter.js — it has rebuilt the whole thing and is
       saying something more specific than a count.

       And every other Noted bar counts its own answer: a quiz teaser's
       answer rows (sections/quiz-teaser.liquid, worked out in Liquid per
       answer — its picks plus its collection), sort a concern, the picks
       section, the quiz banner. They sit inside a .feed-section, so this
       pass found their "N from M" and rewrote it from the section's FIRST
       rail and first View All link — every answer of a teaser then read
       the first answer's numbers ("Frizzy Hair: 5 from 5" over a
       collection of more). Not ours to count. */
    if (el.closest('.vro-noted, .vro-noted-pill, .quiz-style-row')) return;

    var pair = readPair(el);
    if (!pair) return;

    var shown = visibleCards(root);
    /* A rail whose cards have not rendered yet would read "0 from 1400",
       which is worse than the number already there. */
    if (!shown) return;

    var handle = handleFor(root, section);
    if (!handle) {
      /* No collection to count, so only the half we can stand behind is
         corrected. */
      if (shown !== pair.shown) writePair(pair.node, shown, pair.total);
      return;
    }

    totalFor(handle).then(function (total) {
      var fresh = readPair(el);
      if (!fresh) return;
      var now = visibleCards(root);
      if (!now) return;
      writePair(fresh.node, now, total === null ? fresh.total : total);
    });
  }

  /* ---------------------------------------------------------------
     The pill's words, which belong to the open tab as much as its count
  --------------------------------------------------------------- */

  /* The header pill sits beside the heading, which is above the tabs, so it
     cannot live inside the tab it speaks for. The section prints every
     tab's line into it and this shows the open one.

     A tab with nothing to say takes the pill with it rather than leaving an
     empty gradient box beside the heading — the routine rail's "VIEW ALL"
     tab is one.

     `hidden` rather than a class, and read back off the element rather than
     tracked here: the section decides which line is which, and the only
     state is which one is on screen. */
  function paintPillText(section) {
    var pill = section.querySelector('[data-vro-pill]');
    if (!pill) return;
    var lines = pill.querySelectorAll('[data-vro-pill-for]');
    if (!lines.length) return;

    var panes = section.querySelectorAll('.tab-pane');
    var open = 0;
    for (var i = 0; i < panes.length; i++) {
      if (panes[i].classList.contains('active')) { open = i; break; }
    }

    var showing = null;
    for (var j = 0; j < lines.length; j++) {
      var mine = parseInt(lines[j].getAttribute('data-vro-pill-for'), 10) === open;
      lines[j].hidden = !mine;
      if (mine) showing = lines[j];
    }
    pill.hidden = !showing || !showing.textContent.trim();
  }

  function paint() {
    document.querySelectorAll('.feed-section, .rail-section').forEach(function (section) {
      var pane = openPane(section);

      paintPillText(section);

      /* The count above the heading and the one in the heading's pill both
         speak for the row, so both follow whichever tab is open. */
      section.querySelectorAll(':scope > .container-fluid > .feed-rail-header .feed-eyebrow, :scope .feed-eyebrow')
        .forEach(function (el) { paintOne(el, pane, section); });
      section.querySelectorAll('.feed-rail-header-pill .info-pill-count')
        .forEach(function (el) { paintOne(el, pane, section); });

      /* A pill inside a tab speaks for that tab, open or not. */
      section.querySelectorAll('.tab-pane').forEach(function (tab) {
        tab.querySelectorAll('.info-pill .info-pill-count, .info-pill-count')
          .forEach(function (el) { paintOne(el, tab, section); });
      });

      /* An untabbed rail's pill is the rail's. */
      if (!section.querySelector('.tab-pane')) {
        section.querySelectorAll('.info-pill .info-pill-count, .info-pill-count')
          .forEach(function (el) { paintOne(el, railIn(section), section); });
      }
    });
  }

  /* Re-run whenever the page could have changed what is on screen: a tab
     switched, a question answered, the weather rail narrowed itself. All
     three are other people's events, so this listens rather than asking to
     be called — nothing else has to know this file exists. */
  var queued = null;
  function repaint() {
    if (queued) clearTimeout(queued);
    queued = setTimeout(function () { queued = null; paint(); }, 120);
  }

  function watch() {
    document.addEventListener('click', function (event) {
      if (!event.target.closest) return;
      if (event.target.closest('.rail-tab-btn, [data-vro-facet-option], [data-vro-reselect], .size-quiz-btn')) {
        repaint();
        /* Answering runs an animation the counts sit on the far side of. */
        setTimeout(repaint, 800);
      }
    });
    document.addEventListener('change', function (event) {
      if (event.target && event.target.hasAttribute &&
          event.target.hasAttribute('data-vro-weather-switch')) repaint();
    });
    /* Bootstrap's own tab event, for a tab changed by anything but a click. */
    document.addEventListener('shown.bs.tab', repaint);

    /* And whatever else rebuilds a rail.

       The click list above names the controls this file knew about when it
       was written, which is a list that goes stale: the routine rail's count
       still read "5 from 32" after the skin question had swapped six cards
       into it, because the answer came from the quiz modal rather than from
       any of those four selectors. Watching the rails themselves means the
       count follows whatever put the cards there — a tab, a question, the
       weather, a fetch that has not been written yet.

       Only childList, and only on the rails: a rail that gains or loses a
       card is exactly the event this file exists for, and nothing here reacts
       to attributes or text, so nothing it writes can feed back into it. */
    if (!('MutationObserver' in window)) return;
    var observer = new MutationObserver(repaint);
    document.querySelectorAll('.feed-section, .rail-section').forEach(function (section) {
      /* The whole section, because a rail is sometimes replaced rather than
         refilled, and an observer bound to the node that went away sees
         nothing after that.

         childList only. What this file writes is a text node's contents,
         which is a characterData mutation and not one of these — so it
         cannot set itself off again. */
      observer.observe(section, { childList: true, subtree: true });
    });
  }

  function start() {
    paint();
    watch();
    /* The weather rail decides what it is showing after its own request
       lands, which is later than this. */
    setTimeout(paint, 1200);
    setTimeout(paint, 3000);
  }

  window.VroRailCounts = { paint: paint, repaint: repaint, totalFor: totalFor };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
